import { makeAutoObservable } from "mobx";
import { token } from "api/auth";
import type { FntClaimInfo } from "api/types/fntClaim";
import type { authSteps } from "api/types/auth";
import type {
  UserInterface,
  UserStatisticsInterface,
  UserFeesAndLimitsInterface,
  RequestedForm,
  UserCompanyData,
  UserLevelFlow,
} from "helpers/types";

const initialUserState: UserInterface = {
  id: "",
  uid: "",
  email: "",
  token: "",
  referralCode: "",
  emailVerified: false,
  level: "BASIC",
  type: "REGULAR",
  canAccessCompanyAccount: false,
};

const mockedUserState = {
  canAccessCompanyAccount: false,
  companyId: 40,
  email: "demo_fin@demo.com",
  emailVerified: true,
  id: 1561,
  level: "SENIOR" as "SENIOR",
  referralCode: "2049750c459d",
  type: "REGULAR" as "REGULAR",
  uid: "2B31AD",

  token: "",
};

class Store {
  user: UserInterface = mockedUserState;
  authStep: authSteps | null = null;
  authSecret: string | null = null;
  authOneTimeAccessToken: string | null = null;
  isMFAEnabled: boolean = false;
  isAuthenticated: boolean = false;
  isLoggedAsAdmin: boolean = false;
  isLoggedFromTelegram: boolean = false;
  telegramLoginError: string | null = null;
  isTelegramLoginModalErrorIsOpen: boolean = false;
  statistics: UserStatisticsInterface | null = null;
  feesAndLimits: UserFeesAndLimitsInterface | null = null;
  requestedForms: RequestedForm[] | null = null;
  userCompanyInfo: UserCompanyData | null = null;
  userLevelFlowInfo: UserLevelFlow | null = null;
  claimInfo: FntClaimInfo[] | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  setAuthenticated = () => {
    this.isAuthenticated = true;
  };

  setUserData = (data: UserInterface) => {
    this.user = data;
  };

  setAuthStep = (step: authSteps | null) => {
    this.authStep = step;
  };
  setAuthSecret = (step: string | null) => {
    this.authSecret = step;
  };
  setAuthOneTimeAcessToken = (authOneTimeAccessToken: string | null) => {
    this.authOneTimeAccessToken = authOneTimeAccessToken;
  };

  logOut = () => {
    token.unset();
    this.isLoggedFromTelegram = false;
    this.isLoggedAsAdmin = false;
    localStorage.removeItem("isLoggedAsAdmin");

    if (localStorage.getItem("loggedFromTelegram")) {
      localStorage.removeItem("loggedFromTelegram");
      return window.Telegram!.WebApp.close(); // Closes the Telegram browser
    }

    this.isAuthenticated = false;
    this.user = initialUserState;
  };

  setStatistics = (statistics: UserStatisticsInterface | null) => {
    this.statistics = statistics;
  };

  setFeesAndLimits = (feesAndLimits: UserFeesAndLimitsInterface | null) => {
    this.feesAndLimits = feesAndLimits;
  };

  setRequestedForms = (requestedForms: RequestedForm[] | null) => {
    this.requestedForms = requestedForms;
  };

  setIsLoggedAsAdmin = () => {
    this.isLoggedAsAdmin = true;
  };

  setIsLoggedFromTelegram = () => {
    this.isLoggedFromTelegram = true;
  };

  turnOffMFA = () => {
    this.isMFAEnabled = false;
  };
  turnOnMFA = () => {
    this.isMFAEnabled = true;
  };

  setUserCompanyInfo = (userCompanyInfo: UserCompanyData) => {
    this.userCompanyInfo = userCompanyInfo;
  };

  setTelegramLoginError = (error: string | null) => {
    this.telegramLoginError = error;
  };

  openTelegramLoginErrorModal = () => {
    this.isTelegramLoginModalErrorIsOpen = true;
  };

  setUserLevelFlowInfo = (userLevelFlowInfo: UserLevelFlow | null) => {
    this.userLevelFlowInfo = userLevelFlowInfo;
  };

  setClaimInfo = (claimInfo: FntClaimInfo[] | null) => {
    this.claimInfo = claimInfo;
  };
}

const userStore = new Store();
export default userStore;
